/**
 * BrokerSection Component
 *
 * Single-broker section presented as a "button + capture modal", mirroring
 * the Collateral / Stamp Duty sections for visual consistency:
 *
 *  - When no broker is selected the section header shows "Broker (0)" and
 *    an "Add Broker" button; below is an empty-state Paper.
 *  - When a broker is selected the header shows "Broker (1)", a single-row
 *    table renders the broker / fee % / computed amount with edit + remove
 *    actions. The Add button is hidden (single-broker constraint).
 *
 * The broker FEE is wired to reference data: the modal has a "Broker Fee"
 * dropdown listing the `Broker Fee`-type rows from Reference Data → Placement
 * Fees. Selecting a row sets placement_fees_id (whose GL expense/control
 * accounts drive the commission journal export) and pre-fills the % field,
 * which the user can then override. Broker fee is an upfront, one-off fee =
 * capital_amount × %.
 *
 * The form fields broker_id / broker_fees / placement_fees_id remain
 * authoritative — the modal just writes into them via setValue. is_brokered is
 * kept in sync by an effect on broker_id presence so validation in
 * useContractValidation.js continues to apply.
 *
 * Stamp duty is NOT handled here (T112): it is a standalone option captured by
 * StampDutySection.
 */

import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useFormContext } from 'react-hook-form';
import { useContractFormContext } from '../ContractFormContext';
import StakeholderAutocomplete from '../../../common/StakeholderAutocomplete';
import ConfirmDialog from '../../../common/ConfirmDialog';
import { formatNumber, formatRatePct } from '../../../../utils/formatters';

// T3-084: broker-fee VAT treatment. STANDARD applies the contract country's rate (the
// pre-T3-084 behaviour and the default); NO_VAT is an explicit 0% for a cross-border fee —
// e.g. a broker domiciled in eSwatini on an SZL contract whose fee is settled from SA in ZAR.
// No derived signal can detect that, hence the explicit choice. Codes match the DB CHECK.
const VAT_TREATMENT_STANDARD = 'STANDARD';
const VAT_TREATMENT_NO_VAT = 'NO_VAT';

const EMPTY_FORM = {
  broker_id: '',
  placement_fees_id: '',
  broker_fees: '',
  broker_fee_vat_treatment: VAT_TREATMENT_STANDARD,
  broker_fee_settled_in_other_ccy: false,
  broker_fee_settlement_currency_id: '',
  broker_fee_vat_note: '',
};

// Fixed widths so the fee% + Amount columns sit tight together and Actions
// anchors at the same right-edge position as the Stamp Duty / Collateral tables.
const PCT_COL_W = 120;
// Narrower per-column width so the three amount columns (Excl / VAT / Incl) fit
// where the single Amount column used to sit.
const AMT_SUB_COL_W = 130;
const ACTIONS_COL_W = 96;

function BrokerSection() {
  const { watch, setValue } = useFormContext();
  const { refData, appendToRefData, initialData, isEditable } = useContractFormContext();

  const brokerId = watch('broker_id');
  const brokerFees = watch('broker_fees');
  const placementFeesId = watch('placement_fees_id');
  const isBrokered = !!brokerId;
  const disabled = isEditable === false;

  // Contract capital + currency for the computed (capital × %) amount.
  const capitalAmount = parseFloat(watch('capital_amount')) || 0;
  const investmentCurrencyId = watch('investment_currency_id');
  const currencyCode = (refData.currencies || []).find(c => c.id === investmentCurrencyId)?.currency_code || '';

  // Available Broker Fee reference rows (type = 'Broker Fee', active).
  const brokerFeeOptions = (refData.placementFees || []).filter(
    (f) => f.type === 'Broker Fee' && f.active !== false
  );

  // T3-012: VAT rate for the contract's country (investment currency country).
  // One current rate per country; absent => no VAT configured (treat as 0, but
  // surface a hint rather than silently dropping VAT).
  const vatRow = (refData.vatRates || []).find(
    (v) => v.country_id === investmentCurrencyId && v.active !== false
  );
  const vatPercent = vatRow ? parseFloat(vatRow.vat_rate) : null;
  const hasVatRate = vatPercent != null && isFinite(vatPercent);

  // T3-084: the saved treatment drives the inline table row; the dialog passes its draft
  // treatment explicitly so the breakdown reacts before the form is written.
  const formVatTreatment = watch('broker_fee_vat_treatment') || VAT_TREATMENT_STANDARD;

  // Effective VAT % for a treatment. NO_VAT is a deliberate tax decision, so it is a hard 0
  // regardless of whether the country happens to have a rate configured.
  const effectiveVatPercent = (treatment) => {
    if (treatment === VAT_TREATMENT_NO_VAT) return 0;
    return hasVatRate ? vatPercent : 0;
  };

  // Derive VAT amount + VAT-inclusive total from an exclusive amount.
  const computeVatAmount = (exclusive, treatment = formVatTreatment) => {
    if (exclusive == null || !isFinite(exclusive)) return null;
    return exclusive * effectiveVatPercent(treatment) / 100;
  };
  const computeInclusive = (exclusive, treatment = formVatTreatment) => {
    if (exclusive == null || !isFinite(exclusive)) return null;
    return exclusive + (computeVatAmount(exclusive, treatment) || 0);
  };

  // The Standard option must NAME the rate it will apply. With no refdata_vat_rates row for
  // the contract country there is no rate — say so explicitly rather than rendering "(0%)",
  // which would make Standard visually identical to No-VAT and hide the fact that this is a
  // configuration gap, not a tax decision.
  const standardVatLabel = hasVatRate
    ? `Standard-Rate VAT (${formatRatePct(vatPercent)}%)`
    : 'Standard-Rate VAT (no rate configured for this country)';

  // Sync is_brokered with broker_id presence. Validation in
  // useContractValidation.js still keys off is_brokered.
  useEffect(() => {
    setValue('is_brokered', !!brokerId);
  }, [brokerId, setValue]);

  // Dialog state — separate from the live form so cancel doesn't mutate it.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_FORM });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, onConfirm: null });

  const computeAmount = (pct) => {
    const p = parseFloat(pct);
    if (!isFinite(p)) return null;
    return capitalAmount * p / 100;
  };

  const handleOpenDialog = (mode) => {
    if (mode === 'edit') {
      // Seed placement_fees_id from the form; fall back to matching the saved
      // % to a Broker Fee row so an edit still pre-selects the dropdown.
      let pfId = placementFeesId || '';
      if (!pfId && brokerFees !== '' && brokerFees != null) {
        const match = brokerFeeOptions.find(
          (o) => Math.abs(parseFloat(o.percentage) - parseFloat(brokerFees)) < 0.00005
        );
        if (match) pfId = match.id;
      }
      setDraft({
        broker_id: brokerId || '',
        placement_fees_id: pfId,
        // Normalize to a trimmed value so the field shows 1.5 not 1.5000.
        broker_fees: formatRatePct(brokerFees),
        // T3-084: seed the treatment + its companions from the form. A contract saved before
        // T3-084 carries no treatment, so it opens on Standard-Rate — its historic behaviour.
        broker_fee_vat_treatment: watch('broker_fee_vat_treatment') || VAT_TREATMENT_STANDARD,
        broker_fee_settled_in_other_ccy: watch('broker_fee_settled_in_other_ccy') === true,
        broker_fee_settlement_currency_id: watch('broker_fee_settlement_currency_id') || '',
        broker_fee_vat_note: watch('broker_fee_vat_note') || '',
      });
    } else {
      setDraft({ ...EMPTY_FORM });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setDraft({ ...EMPTY_FORM });
  };

  // Selecting a Broker Fee row sets the ref id and (re)pre-fills the % from that
  // row. Choosing a different row is a deliberate new selection, so it always
  // refreshes the %; the user can then override by typing in the % field after.
  const handleSelectFee = (feeId) => {
    const selected = brokerFeeOptions.find((o) => o.id === feeId);
    setDraft((prev) => ({
      ...prev,
      placement_fees_id: feeId,
      broker_fees: selected ? parseFloat(selected.percentage) : prev.broker_fees,
    }));
  };

  // T3-084: switching back to Standard clears the companions in the draft, so a user who
  // toggles No-VAT -> fills the currency -> switches back -> saves cannot ship orphaned facts
  // (which the SP would strip anyway). Keeps the UI honest with what the DB will hold.
  const handleSelectVatTreatment = (treatment) => {
    setDraft((prev) => ({
      ...prev,
      broker_fee_vat_treatment: treatment,
      ...(treatment === VAT_TREATMENT_STANDARD
        ? {
          broker_fee_settled_in_other_ccy: false,
          broker_fee_settlement_currency_id: '',
          broker_fee_vat_note: '',
        }
        : {}),
    }));
  };

  // T3-084: the flag OWNS the currency — un-ticking clears it so a stale pick can never be
  // saved, and the SP/CHECK would strip it anyway.
  const handleToggleSettledInOtherCcy = (checked) => {
    setDraft((prev) => ({
      ...prev,
      broker_fee_settled_in_other_ccy: checked,
      ...(checked ? {} : { broker_fee_settlement_currency_id: '' }),
    }));
  };

  const isSubmitDisabled = () => {
    if (!draft.broker_id) return true;
    if (!draft.placement_fees_id) return true;
    const fees = parseFloat(draft.broker_fees);
    if (!isFinite(fees) || fees < 0) return true;
    // T3-084: the currency is required only when the user has asserted the fee is settled in
    // a different currency — that is the one case where "which currency" is a real question.
    if (draft.broker_fee_vat_treatment === VAT_TREATMENT_NO_VAT
      && draft.broker_fee_settled_in_other_ccy === true
      && !draft.broker_fee_settlement_currency_id) return true;
    return false;
  };

  const handleSubmit = () => {
    if (isSubmitDisabled()) return;
    const fees = parseFloat(draft.broker_fees);
    setValue('broker_id', parseInt(draft.broker_id, 10));
    setValue('placement_fees_id', parseInt(draft.placement_fees_id, 10));
    setValue('broker_fees', fees);
    // T3-012: snapshot the VAT % (from the contract country) and the three
    // derived amounts so the contract is a stable record. No VAT rate for the
    // country => 0% VAT, exclusive == inclusive.
    // T3-084: a No-VAT treatment snapshots a HARD 0% — it must not record the country rate,
    // or a later reader cannot distinguish a cross-border override from a domestic fee.
    //
    // *** BUSINESS RULE (2026-07-29) — BROKER FEES ARE STRUCK ONCE, AT SUBMISSION. ***
    // This is the ONLY place the broker-fee snapshot is written, and that is DELIBERATE. The fee is
    // the amount agreed with the broker when the contract was submitted; a LATER change to
    // capital_amount does NOT re-strike it, because the broker earned the fee on the original
    // amount. Capital lives on a different wizard step, so it can and does change afterwards.
    //
    // CONSEQUENCE: a contract whose stored broker_fee_exclusive_amount disagrees with
    // capital_amount x broker_fees% is EXPECTED and CORRECT — not stale, not a bug. Real example:
    // MTN-BW-2026-0002 holds a 400,000 fee struck on 20,000,000 of capital that was later reduced
    // to 150,000.
    //
    // THEREFORE: do NOT add a recompute-on-save (here, in buildPayload, or in sp_contract_update),
    // and do NOT "repair" such rows with a data migration. Both were proposed and rejected — they
    // would overwrite correct, commercially-agreed figures. To change an agreed fee, a user re-opens
    // THIS dialog and clicks Update, which is an explicit act.
    const treatment = draft.broker_fee_vat_treatment || VAT_TREATMENT_STANDARD;
    const isNoVat = treatment === VAT_TREATMENT_NO_VAT;
    const exclusive = computeAmount(fees);
    setValue('broker_fee_vat_percent', isNoVat ? 0 : (hasVatRate ? vatPercent : 0));
    setValue('broker_fee_exclusive_amount', exclusive);
    setValue('broker_fee_vat_amount', computeVatAmount(exclusive, treatment));
    setValue('broker_fee_inclusive_amount', computeInclusive(exclusive, treatment));
    setValue('broker_fee_vat_treatment', treatment);
    const settledInOther = isNoVat && draft.broker_fee_settled_in_other_ccy === true;
    setValue('broker_fee_settled_in_other_ccy', settledInOther);
    // The currency belongs to the flag, so it is only persisted when the flag is set.
    setValue('broker_fee_settlement_currency_id', settledInOther ? (draft.broker_fee_settlement_currency_id || '') : '');
    setValue('broker_fee_vat_note', isNoVat ? (draft.broker_fee_vat_note || '').trim() : '');
    handleCloseDialog();
  };

  const clearBrokerFields = () => {
    setValue('broker_id', '');
    setValue('placement_fees_id', '');
    setValue('broker_fees', '');
    setValue('broker_fee_vat_percent', '');
    setValue('broker_fee_exclusive_amount', '');
    setValue('broker_fee_vat_amount', '');
    setValue('broker_fee_inclusive_amount', '');
    // T3-084: reset the treatment to STANDARD, not '' — it backs a NOT NULL column.
    setValue('broker_fee_vat_treatment', VAT_TREATMENT_STANDARD);
    setValue('broker_fee_settled_in_other_ccy', false);
    setValue('broker_fee_settlement_currency_id', '');
    setValue('broker_fee_vat_note', '');
  };

  const handleRemove = () => {
    setConfirmDialog({
      open: true,
      onConfirm: () => {
        clearBrokerFields();
        setConfirmDialog({ open: false, onConfirm: null });
      },
    });
  };

  const brokerName = (refData.brokers || []).find(b => b.id === brokerId)?.name
    || initialData?.broker_name
    || '—';

  const draftFeeRow = brokerFeeOptions.find((o) => o.id === draft.placement_fees_id);
  // Row amounts: exclusive (capital × %), the VAT on it, and the VAT-inclusive
  // total — the same three values the Add/Edit dialog's breakdown card shows.
  const rowExclusive = computeAmount(brokerFees);
  const rowVat = computeVatAmount(rowExclusive);
  const rowInclusive = computeInclusive(rowExclusive);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">
            Broker ({isBrokered ? 1 : 0})
          </Typography>
        </Box>
        {!disabled && !isBrokered && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('add')}
          >
            Add Broker
          </Button>
        )}
      </Box>

      {!isBrokered ? (
        <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
          <Typography color="textSecondary">
            No broker on this contract. Click "Add Broker" to record one.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            {/* 8-column grid: the broker Amount is split into Excl / VAT / Incl,
                consuming two of the blank filler columns the sibling Stamp Duty /
                Collateral tables use, so Actions stays anchored at the right edge.
                Columns 2–3 remain blank fillers. */}
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>Broker</TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: PCT_COL_W }}>Broker Fee %</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: AMT_SUB_COL_W }}>Amount (Excl)</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: AMT_SUB_COL_W }}>Amount (VAT)</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', width: AMT_SUB_COL_W }}>Amount (Incl)</TableCell>
                {!disabled && <TableCell align="center" sx={{ width: ACTIONS_COL_W, minWidth: ACTIONS_COL_W }}>Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" fontWeight="medium">{brokerName}</Typography>
                  </Box>
                </TableCell>
                <TableCell />
                <TableCell />
                <TableCell align="right" sx={{ width: PCT_COL_W }}>
                  <Typography variant="body2">
                    {brokerFees !== '' && brokerFees != null ? `${formatRatePct(brokerFees)}%` : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ width: AMT_SUB_COL_W }}>
                  <Typography variant="body2">
                    {rowExclusive != null ? `${currencyCode} ${formatNumber(rowExclusive)}` : '—'}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ width: AMT_SUB_COL_W }}>
                  <Typography variant="body2">
                    {rowVat != null ? `${currencyCode} ${formatNumber(rowVat)}` : '—'}
                  </Typography>
                  {/* T3-084: without this a 0.00 is ambiguous between a No-VAT override, a
                      missing country rate, and a configured 0% rate. */}
                  {formVatTreatment === VAT_TREATMENT_NO_VAT && (
                    <Chip label="No-VAT" size="small" variant="outlined" sx={{ mt: 0.5, height: 18, fontSize: '0.65rem' }} />
                  )}
                </TableCell>
                <TableCell align="right" sx={{ width: AMT_SUB_COL_W }}>
                  <Typography variant="body2">
                    {rowInclusive != null ? `${currencyCode} ${formatNumber(rowInclusive)}` : '—'}
                  </Typography>
                </TableCell>
                {!disabled && (
                  <TableCell align="center" sx={{ width: ACTIONS_COL_W, minWidth: ACTIONS_COL_W }}>
                    <Tooltip title="Edit broker">
                      <IconButton size="small" onClick={() => handleOpenDialog('edit')}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove broker">
                      <IconButton size="small" color="error" onClick={handleRemove}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                )}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{brokerId ? 'Edit Broker' : 'Add Broker'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={12}>
              <StakeholderAutocomplete
                stakeholderType="broker"
                options={refData.brokers}
                value={draft.broker_id}
                onChange={(id) => setDraft(prev => ({ ...prev, broker_id: id }))}
                onStakeholderCreated={(newBroker) => {
                  appendToRefData('brokers', newBroker);
                  setDraft(prev => ({ ...prev, broker_id: newBroker.id }));
                }}
                label="Broker *"
                countries={refData.currencies}
              />
            </Grid>

            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="broker-fee-ref-label">Broker Fee *</InputLabel>
                <Select
                  labelId="broker-fee-ref-label"
                  label="Broker Fee *"
                  value={draft.placement_fees_id}
                  onChange={(e) => handleSelectFee(e.target.value)}
                >
                  {brokerFeeOptions.length === 0 && (
                    <MenuItem value="" disabled>
                      No broker-fee rates in Reference Data → Placement Fees
                    </MenuItem>
                  )}
                  {brokerFeeOptions.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.reference} — {formatRatePct(o.percentage)}%
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={12}>
              <TextField
                label="Broker Fees (%) *"
                type="number"
                fullWidth
                value={draft.broker_fees}
                onChange={(e) => setDraft(prev => ({ ...prev, broker_fees: e.target.value }))}
                inputProps={{ step: 0.0001, min: 0 }}
                helperText={
                  draftFeeRow
                    ? `Pre-filled from reference data (${formatRatePct(draftFeeRow.percentage)}%) — editable`
                    : 'Pre-filled from reference data when a broker fee is selected — editable'
                }
              />
            </Grid>

            {/* T3-084: VAT treatment sits directly after the % (it modifies how the % is
                grossed up) and ABOVE the breakdown card, so the user sees the control and
                then its effect — not numbers changing above the control that changed them. */}
            <Grid size={12}>
              <FormControl fullWidth>
                <InputLabel id="broker-vat-treatment-label">VAT Treatment</InputLabel>
                <Select
                  labelId="broker-vat-treatment-label"
                  label="VAT Treatment"
                  value={draft.broker_fee_vat_treatment || VAT_TREATMENT_STANDARD}
                  onChange={(e) => handleSelectVatTreatment(e.target.value)}
                >
                  <MenuItem value={VAT_TREATMENT_STANDARD}>{standardVatLabel}</MenuItem>
                  <MenuItem value={VAT_TREATMENT_NO_VAT}>No-VAT (0%)</MenuItem>
                </Select>
                <FormHelperText>
                  {draft.broker_fee_vat_treatment === VAT_TREATMENT_NO_VAT
                    ? 'No VAT on this fee — e.g. a cross-border fee settled outside this contract’s country.'
                    : 'Applies the VAT rate configured for this contract’s country.'}
                </FormHelperText>
              </FormControl>
            </Grid>

            {draft.broker_fee_vat_treatment === VAT_TREATMENT_NO_VAT && (
              <>
                <Grid size={12}>
                  <FormControlLabel
                    control={(
                      <Checkbox
                        checked={draft.broker_fee_settled_in_other_ccy === true}
                        onChange={(e) => handleToggleSettledInOtherCcy(e.target.checked)}
                      />
                    )}
                    label="Fee is settled in a different currency"
                  />
                </Grid>
                {/* T3-084: the currency picker only appears once the flag is ticked — if the
                    fee is NOT in a different currency there is nothing to pick, and showing a
                    live pre-filled picker under an unticked box reads as a contradiction. */}
                {draft.broker_fee_settled_in_other_ccy === true && (
                  <Grid size={12}>
                    <FormControl fullWidth>
                      <InputLabel id="broker-vat-settlement-ccy-label">Settled in *</InputLabel>
                      <Select
                        labelId="broker-vat-settlement-ccy-label"
                        label="Settled in *"
                        value={draft.broker_fee_settlement_currency_id}
                        onChange={(e) => setDraft(prev => ({ ...prev, broker_fee_settlement_currency_id: e.target.value }))}
                      >
                        {(refData.currencies || [])
                          .filter((c) => c.currency_code && c.active !== false)
                          .map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.currency_code} — {c.country}
                            </MenuItem>
                          ))}
                      </Select>
                      <FormHelperText>
                        {draft.broker_fee_settlement_currency_id
                          ? 'The currency / country the fee is actually settled from.'
                          : 'Required when the fee is settled in a different currency.'}
                      </FormHelperText>
                    </FormControl>
                  </Grid>
                )}
                <Grid size={12}>
                  <TextField
                    label="VAT Note (optional)"
                    fullWidth
                    multiline
                    minRows={2}
                    value={draft.broker_fee_vat_note}
                    onChange={(e) => setDraft(prev => ({ ...prev, broker_fee_vat_note: e.target.value }))}
                    placeholder="e.g. Cross-border — fee paid from SA in ZAR"
                    helperText="Recorded on the contract and shown on the contract detail + approval views."
                  />
                </Grid>
              </>
            )}

            {draft.placement_fees_id && computeAmount(draft.broker_fees) != null && (
              <Grid size={12}>
                {/* T3-012: broker-fee VAT breakdown. Exclusive = capital × %,
                    VAT from the contract country, inclusive = exclusive + VAT. */}
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">Exclusive (capital × %)</Typography>
                    <Typography variant="body2">{currencyCode} {formatNumber(computeAmount(draft.broker_fees))}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      {draft.broker_fee_vat_treatment === VAT_TREATMENT_NO_VAT
                        ? 'VAT (0% — No-VAT)'
                        : `VAT${hasVatRate ? ` (${formatRatePct(vatPercent)}%)` : ''}`}
                    </Typography>
                    <Typography variant="body2">{currencyCode} {formatNumber(computeVatAmount(computeAmount(draft.broker_fees), draft.broker_fee_vat_treatment))}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5, borderTop: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" fontWeight="bold">Inclusive</Typography>
                    <Typography variant="body2" fontWeight="bold">{currencyCode} {formatNumber(computeInclusive(computeAmount(draft.broker_fees), draft.broker_fee_vat_treatment))}</Typography>
                  </Box>
                  {/* T3-084: under a deliberate No-VAT choice the "no rate configured" warning
                      would imply the 0% is an accident, so it is gated on Standard and No-VAT
                      gets its own confirming caption. */}
                  {draft.broker_fee_vat_treatment === VAT_TREATMENT_NO_VAT ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                      No-VAT treatment selected — VAT is 0% by explicit choice, not because a rate
                      is missing. Inclusive equals exclusive.
                      {draft.broker_fee_settled_in_other_ccy && draft.broker_fee_settlement_currency_id
                        ? ` Settled in ${(refData.currencies || []).find(c => c.id === draft.broker_fee_settlement_currency_id)?.currency_code || ''}.`
                        : ''}
                    </Typography>
                  ) : !hasVatRate && (
                    <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 1 }}>
                      No VAT rate configured for this contract's country (Reference Data → VAT Rates).
                      VAT treated as 0% — inclusive equals exclusive.
                    </Typography>
                  )}
                </Paper>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} disabled={isSubmitDisabled()}>
            {isBrokered ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmDialog.open}
        title="Remove Broker"
        message="Are you sure you want to remove the broker? Broker fee will be cleared."
        confirmText="Remove"
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ open: false, onConfirm: null })}
      />
    </Box>
  );
}

export default BrokerSection;
